import { z } from 'zod';

/**
 * PRE-78: per-user progress stats for the mobile stats screen (PRE-69).
 * `GET /v1/me/stats` — derived from the caller's own session/roadmap/feedback
 * data. Every field degrades to 0/`[]`/`null` for a brand-new user.
 */

export const userStatsStreakSchema = z.object({
  /** Consecutive active days ending today or yesterday (0 if the streak is broken). */
  current: z.number().int(),
  /** Longest consecutive-active-day run ever. */
  longest: z.number().int(),
  /** PRE-155: streak milestones (7/14/30/50/100). `reached` = highest milestone the
   * current streak has met (null if below the first); `next` = the next one to hit. */
  milestone: z.object({
    reached: z.number().int().nullable(),
    next: z.number().int().nullable(),
  }),
});

export const userStatsSessionsSchema = z.object({
  completedTotal: z.number().int(),
  completedThisWeek: z.number().int(),
});

export const userStatsRoadmapSchema = z.object({
  completedItems: z.number().int(),
  totalItems: z.number().int(),
});

export const scoreTrendPointSchema = z.object({
  /** ISO-8601 timestamp of the feedback report. */
  date: z.string(),
  /** Practice session id when the report was scored in a session; null for assessment-only reports. */
  sessionId: z.string().nullable(),
  conceptMastery: z.number(),
  explanation: z.number(),
  aiDependency: z.number(),
  /** BR-13 weighted total (0–100). Used for Home Last Score (PRE-253). */
  totalScore: z.number(),
});
export type ScoreTrendPoint = z.infer<typeof scoreTrendPointSchema>;

export const aiDependencyChangeSchema = z.object({
  /** Earliest recorded ai_dependency score; null when there are no reports. */
  baseline: z.number().nullable(),
  /** Most recent ai_dependency score; null when there are no reports. */
  latest: z.number().nullable(),
  /** latest - baseline (null when either is missing). */
  delta: z.number().nullable(),
});

/** PRE-155: the soft daily goal (habit anchor) — do at least `target` sessions today. */
export const userStatsDailyGoalSchema = z.object({
  target: z.number().int(),
  done: z.number().int(),
  met: z.boolean(),
});

/** PRE-166: running per-skill mastery from knowledge-tracing lite (PRE-160). */
export const userStatsSkillMasterySchema = z.object({
  skillId: z.string(),
  title: z.string(),
  /** 0–100 recency-weighted concept mastery. */
  mastery: z.number(),
  /** How many graded reports contributed (signal confidence). */
  count: z.number().int(),
});
export type UserStatsSkillMastery = z.infer<typeof userStatsSkillMasterySchema>;

export const userStatsSchema = z.object({
  streak: userStatsStreakSchema,
  /** PRE-155: today's soft goal + progress. */
  dailyGoal: userStatsDailyGoalSchema,
  sessions: userStatsSessionsSchema,
  roadmap: userStatsRoadmapSchema,
  /** Chronological (oldest → newest), capped to the most recent points. */
  scoreTrend: z.array(scoreTrendPointSchema),
  aiDependency: aiDependencyChangeSchema,
  /** PRE-166: per-skill running mastery (empty for new users). */
  /** PRE-116: legacy twin of `skillMastery`, carrying the same rows. */
  skillMastery: z.array(userStatsSkillMasterySchema),
});
export type UserStats = z.infer<typeof userStatsSchema>;

export const userStatsResponseSchema = z.object({
  ok: z.literal(true),
  data: userStatsSchema,
});
export type UserStatsResponse = z.infer<typeof userStatsResponseSchema>;
